import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import type { TAttachmentColumns } from '@/features/_columns/attachment-table-column'
import { attachmentColumns } from '@/features/_columns/attachment-table-column'
import { DataTable } from '@/features/_components/data-table'
import { api } from '@/features/_lib/api'
import { unwrapApiResult } from '@/features/_lib/react-query/helpers'
import { mutations } from '@/features/_lib/react-query/mutations'
import type { TExposedAttachmentDTO } from '@doutorsim/shared/exposed/application-attachments'

interface AttachmentsTableProps {
  applicationId: string
  attachments: TExposedAttachmentDTO[]
  userType: 'staff' | 'tenant'
}

export const AttachmentsTable = ({
  applicationId,
  attachments,
  userType,
}: AttachmentsTableProps) => {
  const [loadingId, setLoadingId] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const deleteMutation = useMutation(mutations.attachments.delete(queryClient))

  const getDownloadUrl = async (attachmentId: string) => {
    const result = unwrapApiResult(await api.attachments.findById(applicationId, attachmentId))
    return result.downloadUrl
  }

  const handleView = async (attachmentId: string) => {
    setLoadingId(attachmentId)
    try {
      const downloadUrl = await getDownloadUrl(attachmentId)
      window.open(downloadUrl, '_blank')
    } catch {
      toast.error('Erro ao abrir o arquivo')
    } finally {
      setLoadingId(null)
    }
  }

  const handleDownload = async (attachmentId: string) => {
    setLoadingId(attachmentId)
    try {
      const downloadUrl = await getDownloadUrl(attachmentId)
      const attachment = attachments.find((a) => a.id === attachmentId)

      const response = await fetch(downloadUrl)
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = attachment?.filename ?? 'download'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Erro ao baixar o arquivo')
    } finally {
      setLoadingId(null)
    }
  }

  const handleDelete = (attachmentId: string, onSuccess: () => void) => {
    deleteMutation.mutate(
      { applicationId, attachmentId },
      {
        onSuccess: () => {
          toast.success('Arquivo removido com sucesso')
          onSuccess()
        },
        onError: () => toast.error('Erro ao remover o arquivo'),
      },
    )
  }

  const data: TAttachmentColumns[] = attachments.map((attachment) => ({
    id: attachment.id,
    filename: attachment.filename,
    type: attachment.type,
    sizeBytes: attachment.sizeBytes,
    createdAt: attachment.createdAt,
    userType,
    onView: handleView,
    onDownload: handleDownload,
    loadingId,
    onDelete: handleDelete,
    isDeleting: deleteMutation.isPending,
    deletingId: deleteMutation.variables?.attachmentId ?? null,
  }))

  return <DataTable columns={attachmentColumns} data={data} />
}
